import React from "react";
import RightworkSelect from "../../../components/shared/RightworkSelect";
import Tooltip from "@mui/material/Tooltip";
import AddIcon from "@mui/icons-material/Add";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import HistoryIcon from "@mui/icons-material/History";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Drawer from "@mui/material/Drawer";
import DrawerHeader from "../requests/shared/helpers/DrawerHeader";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import LaborModelTestModal from "./LaborModelTestModal";
import { PurpleSwitch } from "../../../helpers/helpers";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import InputAdornment from "@mui/material/InputAdornment";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { ThemeProvider } from "@mui/material/styles";
import { createRightworkTheme } from "../../../theme/rightworkTheme";
import { createDayArray } from "../../../helpers/helpers";
import {
  generateTimeOptionsDF,
  generateBufferOptionsDF,
} from "../../../helpers/helpers";
import { get_t_str } from "../../../helpers/helpers";

const REVENUE_DRIVER_TARGET_COLUMNS = new Set([
  "Net Price",
  "Gross Price",
  "Net Amount",
]);

const LABOR_STANDARD_GROUP_COMPANY_WIDE_KEY = "__company_wide__";

const LABOR_STANDARD_TYPE_ORDER = {
  task: 0,
  routine: 1,
  staffing: 2,
};

const formatCompactNumber = (value, maximumFractionDigits = 2) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return String(value);
  }

  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits,
  }).format(numericValue);
};

const formatNumberForInput = (value, maximumFractionDigits = 8) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return String(value);
  }

  return Number(numericValue.toFixed(maximumFractionDigits)).toString();
};

const formatLaborDuration = (minutes) => {
  if (minutes === null || minutes === undefined || minutes === "") {
    return "0 min";
  }

  const numericMinutes = Number(minutes);
  if (Number.isNaN(numericMinutes)) {
    return `${minutes} min`;
  }

  const absoluteMinutes = Math.abs(numericMinutes);
  if (absoluteMinutes > 0 && absoluteMinutes < 0.01) {
    return `${formatCompactNumber(numericMinutes, 4)} min`;
  }

  return `${formatCompactNumber(numericMinutes, 2)} min`;
};

const isRevenueLaborDriver = (laborDriver) => {
  const salesDriver = laborDriver?.labor_driver_sales;
  const normalizedName = (laborDriver?.name || "").trim().toLowerCase();
  const hasRevenueName =
    normalizedName === "revenue" ||
    normalizedName === "sales" ||
    normalizedName.endsWith(" sales") ||
    normalizedName.includes("revenue");

  return (
    salesDriver?.aggregate_type === "sum" &&
    (REVENUE_DRIVER_TARGET_COLUMNS.has(salesDriver?.target_column) ||
      hasRevenueName)
  );
};

const getScalableDriverScale = (laborDriver) =>
  isRevenueLaborDriver(laborDriver) ? 100 : 1;

const getScalableDriverBasis = (laborDriver) =>
  isRevenueLaborDriver(laborDriver)
    ? "$100 revenue"
    : laborDriver?.name || "driver";

const getScalableInputAdornment = (laborDriver) =>
  isRevenueLaborDriver(laborDriver) ? "min / $100" : "min";

const formatScalableInputValue = (minutesPerUnit, laborDriver) => {
  if (
    minutesPerUnit === null ||
    minutesPerUnit === undefined ||
    minutesPerUnit === ""
  ) {
    return "";
  }

  const numericMinutes = Number(minutesPerUnit);
  if (Number.isNaN(numericMinutes)) {
    return String(minutesPerUnit);
  }

  return formatNumberForInput(
    numericMinutes * getScalableDriverScale(laborDriver)
  );
};

const parseScalableInputValue = (displayValue, laborDriver) => {
  if (
    displayValue === null ||
    displayValue === undefined ||
    displayValue === "" ||
    displayValue === "-" ||
    displayValue === "." ||
    displayValue === "-."
  ) {
    return "";
  }

  const numericValue = Number(displayValue);
  if (Number.isNaN(numericValue)) {
    return "";
  }

  return formatNumberForInput(
    numericValue / getScalableDriverScale(laborDriver)
  );
};

const formatScalableRate = (minutesPerUnit, laborDriver) => {
  if (
    minutesPerUnit === null ||
    minutesPerUnit === undefined ||
    minutesPerUnit === ""
  ) {
    return {
      duration: "0 min",
      basis: getScalableDriverBasis(laborDriver),
    };
  }

  const numericMinutes = Number(minutesPerUnit);
  const scaledMinutes = Number.isNaN(numericMinutes)
    ? minutesPerUnit
    : numericMinutes * getScalableDriverScale(laborDriver);

  return {
    duration: formatLaborDuration(scaledMinutes),
    basis: getScalableDriverBasis(laborDriver),
  };
};

const getRoleSortValue = (laborStandard, roleNameById) => {
  const roleIds = Array.isArray(laborStandard.role_ids)
    ? laborStandard.role_ids
    : [];

  if (roleIds.length === 0) {
    return {
      roleScopeOrder: 0,
      roleNames: "",
    };
  }

  const roleNames = roleIds
    .map((roleId) => roleNameById.get(String(roleId)) || "")
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b))
    .join(", ");

  return {
    roleScopeOrder: 1,
    roleNames,
  };
};

const compareLaborStandards = (a, b, roleNameById) => {
  const typeComparison =
    (LABOR_STANDARD_TYPE_ORDER[a.type] ?? 99) -
    (LABOR_STANDARD_TYPE_ORDER[b.type] ?? 99);
  if (typeComparison !== 0) return typeComparison;

  const departmentComparison = (a.department?.role_type || "").localeCompare(
    b.department?.role_type || ""
  );
  if (departmentComparison !== 0) return departmentComparison;

  const aRoleSortValue = getRoleSortValue(a, roleNameById);
  const bRoleSortValue = getRoleSortValue(b, roleNameById);
  const roleScopeComparison =
    aRoleSortValue.roleScopeOrder - bRoleSortValue.roleScopeOrder;
  if (roleScopeComparison !== 0) return roleScopeComparison;

  const roleComparison = aRoleSortValue.roleNames.localeCompare(
    bRoleSortValue.roleNames
  );
  if (roleComparison !== 0) return roleComparison;

  const nameComparison = (a.name || "").localeCompare(b.name || "");
  if (nameComparison !== 0) return nameComparison;

  return (a.displayIndex || 0) - (b.displayIndex || 0);
};

const groupLaborStandardsByProgramFlag = (laborStandards, roleNames = []) => {
  const groupsByKey = new Map();
  const roleNameById = new Map(
    (roleNames || []).map((role) => [String(role.id), role.role_name || ""])
  );

  (laborStandards || []).forEach((laborStandard, index) => {
    const flag = laborStandard.labor_program_flag_company;
    const groupKey = flag?.uuid || LABOR_STANDARD_GROUP_COMPANY_WIDE_KEY;

    if (!groupsByKey.has(groupKey)) {
      groupsByKey.set(groupKey, {
        key: groupKey,
        name: flag?.name || "Company-wide",
        label: flag?.name ? `Stores with ${flag.name}` : "Company-wide",
        isCompanyWide: !flag?.uuid,
        standards: [],
      });
    }

    groupsByKey.get(groupKey).standards.push({
      ...laborStandard,
      displayIndex: index,
    });
  });

  groupsByKey.forEach((group) => {
    group.standards.sort((a, b) =>
      compareLaborStandards(a, b, roleNameById)
    );
  });

  return Array.from(groupsByKey.values()).sort((a, b) => {
    if (a.isCompanyWide && !b.isCompanyWide) return -1;
    if (!a.isCompanyWide && b.isCompanyWide) return 1;
    return a.name.localeCompare(b.name);
  });
};

function LaborModelInnerDisplay(props) {
  const defaultTimeWindow = {
    dynamic_end_time: null,
    dynamic_start_time: null,
    fixed_end_offset_qhnum: 0,
    fixed_start_offset_qhnum: 0,
    name: "",
    store_time_window: [],
    uuid: null,
  };

  const createDefaultTimeWindow = () => ({ ...defaultTimeWindow });

  const default_data = {
    constant_time_minutes: null,
    department: {
      company: null,
      role_type: "",
    },
    labor_driver: {
      labor_driver_attribute: null,
      labor_driver_sales: null,
      name: "",
      uuid: null,
    },
    labor_program_flag_company: {
      default: null,
      name: "",
      uuid: null,
    },
    name: "",
    role_ids: [],
    scalable_time_minutes: null,
    staffing_people_count: null,
    is_minimum_staffing: false,
    is_maximum_staffing: false,
    time_window: {
      ...createDefaultTimeWindow(),
    },
    type: "",
    uuid: null,
    week_days: [true, true, true, true, true, true, true],
  };
  const [publishedRowTooltipIndex, setPublishedRowTooltipIndex] =
    React.useState(null);
  const publishedRowTooltipTimeoutRef = React.useRef(null);

  React.useEffect(() => {
    return () => {
      if (publishedRowTooltipTimeoutRef.current) {
        window.clearTimeout(publishedRowTooltipTimeoutRef.current);
      }
    };
  }, []);

  const getStandardTypeLabel = (type) => {
    if (type === "task") return "support";
    if (type === "routine") return "service";
    if (type === "staffing") return "staffing";
    return "standard";
  };

  const handleChangeLayer1 = (event) => {
    const { name, value } = event.target;
    setSubmitError("");
    setModalData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleChangeLaborDriver = (event) => {
    const selectedLaborDriver = props.laborDrivers?.find(
      (driver) => driver.name === event.target.value
    );
    setSubmitError("");
    setModalData((prevData) => ({
      ...prevData,
      labor_driver: {
        ...selectedLaborDriver,
      },
    }));
  };

  const handleDepartmentChange = (event) => {
    const selectedDepartment = props.roleTypes?.find(
      (department) => department.role_type === event.target.value
    );
    setSubmitError("");
    setModalData((prevData) => ({
      ...prevData,
      department: {
        ...selectedDepartment,
      },
    }));
  };

  const handleTimeWindowChange = (event) => {
    const selectedTimeWindow = props.companyTimeWindows?.find(
      (timeWindow) => timeWindow.name === event.target.value
    );
    setSubmitError("");
    setModalData((prevData) => ({
      ...prevData,
      time_window: {
        ...selectedTimeWindow,
      },
    }));
  };

  const handleTimeWindowStartStopChange = (event) => {
    const { name, value } = event.target;
    setModalData((prevData) => ({
      ...prevData,
      time_window: {
        ...prevData.time_window,
        [name]: value,
      },
    }));
  };

  const handleOpenCreateTimeWindow = () => {
    setShowCreateTimeWindow(true);
    setIsEditingTimeWindow(false);
    setTimeWindowBackup(null);
    setModalData((prevData) => ({
      ...prevData,
      time_window: {
        ...createDefaultTimeWindow(),
      },
    }));
    setSelectedStartTime("");
    setSelectedEndTime("");
  };

  const handleCreateTimeWindow = (event) => {
    const { name, value } = event.target;
    setSubmitError("");
    setModalData((prevData) => ({
      ...prevData,
      time_window: {
        ...prevData.time_window,
        [name]: value,
      },
    }));
  };

  const handleCloseCreateTimeWindow = () => {
    setShowCreateTimeWindow(false);
    setSelectedStartTime("");
    setSelectedEndTime("");
    setModalData((prevData) => ({
      ...prevData,
      time_window: timeWindowBackup
        ? { ...timeWindowBackup }
        : createDefaultTimeWindow(),
    }));
    setIsEditingTimeWindow(false);
    setTimeWindowBackup(null);
  };

  const [laborStandards, setLaborStandards] = React.useState(
    props.laborStandards
  );

  const [laborStandardsSandbox, setLaborStandardsSandbox] = React.useState(
    props.laborStandardsSandbox
  );

  // console.log("LABOR STANDARDS SANDBOX", laborStandardsSandbox)

  React.useEffect(() => {
    setLaborStandards(props.laborStandards);
    setLaborStandardsSandbox(props.laborStandardsSandbox);
  }, [props.laborStandards, props.laborStandardsSandbox]);

  // Environment Dropdown

  const [environmentType, setEnvironmentType] = React.useState(
    props.readOnly ? "published" : props.scenarioMode ? "sandbox" : "published"
  );

  React.useEffect(() => {
    if (props.readOnly) {
      setEnvironmentType("published");
    } else if (props.scenarioMode) {
      setEnvironmentType("sandbox");
    }
  }, [props.readOnly, props.scenarioMode]);

  React.useEffect(() => {
    if (props.sandboxRestoreToken) {
      setEnvironmentType("sandbox");
    }
  }, [props.sandboxRestoreToken]);

  // Labor Standard Dropdown
  const [isLaborStandardDropdownOpen, setIsLaborStandardDropdownOpen] =
    React.useState(false);
  const dropdownLaborStandardRef = React.useRef(null);

  const handleLaborStandardButtonClick = () => {
    setIsLaborStandardDropdownOpen(!isLaborStandardDropdownOpen);
  };

  const handleClickOutside = (event) => {
    if (
      dropdownLaborStandardRef.current &&
      !dropdownLaborStandardRef.current.contains(event.target)
    ) {
      setIsLaborStandardDropdownOpen(false);
    }
  };

  React.useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const [drawerType, setDrawerType] = React.useState("");

  const [openLaborStandardDrawer, setOpenLaborStandardDrawer] =
    React.useState(false);

  const handleOpenLaborStandardDrawer = (type) => {
    setIsLaborStandardDropdownOpen(false);
    setOpenLaborStandardDrawer(true);
    setDrawerType(type);
    setModalData((prevData) => {
      const newData = {
        ...prevData,
        type: type,
      };
      if (type === "staffing") {
        newData.constant_time_minutes = null;
        newData.scalable_time_minutes = null;
        newData.labor_driver = {
          labor_driver_attribute: null,
          labor_driver_sales: null,
          name: "",
          uuid: null,
        };
        if (newData.staffing_people_count == null) {
          newData.staffing_people_count = 1;
        }
        if (!newData.uuid || newData.is_minimum_staffing == null) {
          newData.is_minimum_staffing = true;
        }
      } else {
        newData.staffing_people_count = null;
        newData.is_minimum_staffing = false;
        newData.is_maximum_staffing = false;
      }
      setScaleWithDriverChecked(newData.labor_driver?.name?.length > 0);
      setLocationSelectionChecked(newData.labor_program_flag_company?.uuid);
      return newData;
    });
  };

  const handleCloseLaborStandardDrawer = () => {
    setOpenLaborStandardDrawer(false);
    handleCloseCreateTimeWindow();
    setScaleWithDriverChecked(false);
    setScalableTimeInputValue("");
    setSubmitError("");
    setRoleSelectionChecked(false);
    setDaySelectionChecked(false);
    setLocationSelectionChecked(false);
  };
  const [modalData, setModalData] = React.useState(default_data);
  const [modalEdit, setModalEdit] = React.useState(false);
  const [submitError, setSubmitError] = React.useState("");

  const [openTestModal, setOpenTestModal] = React.useState(false);
  const handleOpenTestModal = () => setOpenTestModal(true);
  const handleCloseTestModal = () => setOpenTestModal(false);

  const [scaleWithDriverChecked, setScaleWithDriverChecked] = React.useState();
  const [scalableTimeInputValue, setScalableTimeInputValue] =
    React.useState("");
  const scalableTimeInputFocusedRef = React.useRef(false);

  // console.log(modalData.labor_driver?.name?.length > 0);
  // console.log(scaleWithDriverChecked);

  React.useEffect(() => {
    if (
      !openLaborStandardDrawer ||
      drawerType === "staffing" ||
      scalableTimeInputFocusedRef.current
    ) {
      return;
    }

    setScalableTimeInputValue(
      formatScalableInputValue(
        modalData.scalable_time_minutes,
        modalData.labor_driver
      )
    );
  }, [
    openLaborStandardDrawer,
    drawerType,
    modalData.uuid,
    modalData.labor_driver,
    modalData.scalable_time_minutes,
  ]);

  React.useEffect(() => {
    // Update scaleWithDriverChecked based on modalData
    if (modalData.labor_driver?.name?.length > 0) {
      setScaleWithDriverChecked(true);
    }
  }, [modalData]);

  const handleScaleWithDriverChecked = (event) => {
    const isChecked = event.target.checked;
    setScaleWithDriverChecked(isChecked);
    if (!isChecked) {
      // Clear relevant data when unchecked
      setModalData((prevData) => ({
        ...prevData,
        scalable_time_minutes: null,
        labor_driver: {
          ...prevData.labor_driver,
          labor_driver_attribute: null,
          labor_driver_sales: null,
          name: "",
          uuid: null,
        },
      }));
      setScalableTimeInputValue("");
    }
  };

  const handleScalableTimeDisplayChange = (event) => {
    const { value } = event.target;
    setScalableTimeInputValue(value);
    setModalData((prevData) => ({
      ...prevData,
      scalable_time_minutes: parseScalableInputValue(
        value,
        prevData.labor_driver
      ),
    }));
  };

  const handleScalableTimeDisplayBlur = () => {
    scalableTimeInputFocusedRef.current = false;
    setScalableTimeInputValue(
      formatScalableInputValue(
        modalData.scalable_time_minutes,
        modalData.labor_driver
      )
    );
  };

  const [showCreateTimeWindow, setShowCreateTimeWindow] = React.useState(false);
  const [isEditingTimeWindow, setIsEditingTimeWindow] = React.useState(false);
  const [timeWindowBackup, setTimeWindowBackup] = React.useState(null);

  const [selectedStartTime, setSelectedStartTime] = React.useState("");
  const [selectedEndTime, setSelectedEndTime] = React.useState("");
  const [timeWindowSaving, setTimeWindowSaving] = React.useState(false);

  const handleEditExistingTimeWindow = () => {
    if (!modalData.time_window?.uuid) {
      return;
    }

    const existingTimeWindow =
      props.companyTimeWindows?.find(
        (timeWindow) => timeWindow.uuid === modalData.time_window.uuid
      ) || modalData.time_window;

    setTimeWindowBackup({ ...existingTimeWindow });
    setModalData((prevData) => ({
      ...prevData,
      time_window: {
        ...existingTimeWindow,
      },
    }));
    setSelectedStartTime(existingTimeWindow.dynamic_start_time || "");
    setSelectedEndTime(existingTimeWindow.dynamic_end_time || "");
    setIsEditingTimeWindow(true);
    setShowCreateTimeWindow(true);
  };

  const handleSaveEditedTimeWindow = async () => {
    if (!modalData.time_window?.uuid) {
      return;
    }

    setTimeWindowSaving(true);
    try {
      const updatedTimeWindow = await props.edit_time_window({
        company_time_window_uuid: modalData.time_window?.uuid,
        name: modalData.time_window?.name,
        dynamic_start_time: modalData.time_window?.dynamic_start_time,
        dynamic_end_time: modalData.time_window?.dynamic_end_time,
        fixed_start_offset_qhnum: modalData.time_window?.fixed_start_offset_qhnum,
        fixed_end_offset_qhnum: modalData.time_window?.fixed_end_offset_qhnum,
      });

      if (updatedTimeWindow) {
        setModalData((prevData) => ({
          ...prevData,
          time_window: {
            ...prevData.time_window,
            ...updatedTimeWindow,
          },
        }));
        setIsEditingTimeWindow(false);
        setTimeWindowBackup(null);
        setShowCreateTimeWindow(false);
        setSelectedStartTime("");
        setSelectedEndTime("");
      }
    } finally {
      setTimeWindowSaving(false);
    }
  };

  const setSelectedStartTimeAndUpdateDynamicTime = (value) => {
    // Update selected start time
    setSelectedStartTime(value);

    // Determine the dynamic_start_time based on the value
    let dynamicStartTime;
    if (value === "start") {
      dynamicStartTime = "start";
    } else if (value === "close") {
      dynamicStartTime = "close";
    } else {
      dynamicStartTime = "not_dynamic";
    }

    // Update dynamic_start_time in state
    setModalData((prevData) => ({
      ...prevData,
      time_window: {
        ...prevData.time_window,
        dynamic_start_time: dynamicStartTime,
      },
    }));
  };

  const setSelectedEndTimeAndUpdateDynamicTime = (value) => {
    // Update selected start time
    setSelectedEndTime(value);

    // Determine the dynamic_start_time based on the value
    let dynamicEndTime;
    if (value === "start") {
      dynamicEndTime = "start";
    } else if (value === "close") {
      dynamicEndTime = "close";
    } else {
      dynamicEndTime = "not_dynamic";
    }

    // Update dynamic_start_time in state
    setModalData((prevData) => ({
      ...prevData,
      time_window: {
        ...prevData.time_window,
        dynamic_end_time: dynamicEndTime,
      },
    }));
  };

  // console.log("modalData", modalData);

  const [roleSelectionChecked, setRoleSelectionChecked] = React.useState(false);

  const handleRoleSelectionChecked = (event) => {
    const isChecked = event.target.checked;
    setRoleSelectionChecked(isChecked);
    if (!isChecked && modalData.is_maximum_staffing) {
      setModalData((previous) => ({ ...previous, role_ids: [] }));
    }
  };

  // const [selectedRoles, setSelectedRoles] = React.useState([]);

  // // console.log("SELECTED ROLES", selectedRoles)

  const handleRoleSelectionChange = (event) => {
    const {
      target: { value },
    } = event;
    // setSelectedRoles(value);
    setModalData((prevData) => ({
      ...prevData,
      role_ids: value,
    }));
  };

  const roleNames = Array.isArray(props.roleNames) ? props.roleNames : [];

  const filteredRoleNames = roleNames.filter(
    (role) => role.role_type === modalData.department?.role_type
  );

  // console.log("ROLENAMES", props.roleNames);

  const [daySelectionChecked, setDaySelectionChecked] = React.useState(false);

  React.useEffect(() => {
    setDaySelectionChecked(modalData.week_days?.some((day) => day === false));
    setRoleSelectionChecked(modalData.role_ids?.length > 0);
  }, [modalData]);

  const handleDaySelectionChecked = (event) => {
    const isChecked = event.target.checked;
    setDaySelectionChecked(isChecked);
    if (!isChecked) {
      setModalData((prevData) => ({
        ...prevData,
        week_days: [true, true, true, true, true, true, true],
      }));
    }
  };

  const [locationSelectionChecked, setLocationSelectionChecked] =
    React.useState(modalData?.labor_program_flag_company?.uuid !== null);

  // console.log("LOCATION SELECTION CHECKED", locationSelectionChecked)
  // console.log("TEST TEST", modalData?.labor_program_flag_company?.uuid !== null)

  const handleLocationSelectionChecked = (event) => {
    const isChecked = event.target.checked;
    setSubmitError("");
    setLocationSelectionChecked(isChecked);
    if (!isChecked) {
      setModalData((prevData) => ({
        ...prevData,
        labor_program_flag_company: {
          default: null,
          name: "",
          uuid: null,
        },
      }));
    }
  };

  const handleFlagChange = (event) => {
    const selectedFlag = props.laborProgramFlags?.find(
      (flag) => flag.name === event.target.value
    );
    setSubmitError("");
    setModalData((prevData) => ({
      ...prevData,
      labor_program_flag_company: {
        ...selectedFlag,
      },
    }));
  };

  // console.log("FLAGS", props.laborProgramFlags);

  const day_abbrevs = createDayArray(props.businessWeekStartDayIndex);

  // console.log(day_abbrevs);

  const theme = createRightworkTheme({
    status: {
      danger: "#e53e3e",
    },
    palette: {
      neutral: {
        main: "#64748B",
        contrastText: "#fff",
      },
    },
  });

  function handleAppliedToChange(day_i) {
    let appliedToCopy = [...modalData.week_days];
    appliedToCopy[day_i] = !modalData.week_days[day_i];
    // setAppliedTo(appliedToCopy);
    setModalData((prevData) => ({
      ...prevData,
      week_days: appliedToCopy,
    }));
  }

  function appliedToButtonGroup() {
    return (
      <div className="w-full mt-2 bg-slate-50 rounded-lg flex">
        <ThemeProvider theme={theme}>
          <ButtonGroup
            style={{
              flex: 1,
              border: "1px solid #f1f5f9",
              borderRadius: "7px",
            }}
          >
            {modalData.week_days?.map((d, i) => (
              <Button
                fullWidth
                color="neutral"
                size="small"
                style={{
                  border: d ? "1px solid #6d28d9" : "1px solid #f8fafc",
                  borderRadius: "7px",
                  backgroundColor: d ? "#ede9fe" : "transparent",
                  padding: "6px",
                  margin: "0 0.5px",
                }}
                key={i}
                onClick={() => handleAppliedToChange(i)}
              >
                {day_abbrevs[i]}
              </Button>
            ))}
          </ButtonGroup>
        </ThemeProvider>
      </div>
    );
  }

  function getSelectedDays(daysArray, selectionArray) {
    const selectedDays = [];
    for (let i = 0; i < daysArray.length; i++) {
      if (selectionArray[i]) {
        selectedDays.push(daysArray[i]);
      }
    }
    return selectedDays;
  }

  const handleStaffingModeChange = (event) => {
    const mode = event.target.value;
    setModalData((previous) => ({
      ...previous,
      is_minimum_staffing: mode === "minimum",
      is_maximum_staffing: mode === "maximum",
    }));
  };

  const parseNullableFloat = (value) => {
    if (value === null || value === undefined || value === "") {
      return null;
    }
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const parseNullableInteger = (value) => {
    if (value === null || value === undefined || String(value).trim() === "") {
      return null;
    }
    const parsed = Number(value);
    return Number.isInteger(parsed) ? parsed : null;
  };

  const validateLaborStandard = () => {
    if (!modalData.name?.trim()) {
      return "Name this labor standard before saving.";
    }
    if (modalData.name.trim().length > 255) {
      return "Labor standard names must be 255 characters or fewer.";
    }

    if (!modalData.department?.id) {
      return "Assign this standard to a department before saving.";
    }

    if (locationSelectionChecked && !modalData.labor_program_flag_company?.uuid) {
      return "Choose the store group this standard applies to.";
    }

    if (!modalData.time_window?.name?.trim()) {
      return "Choose or create a time window before saving.";
    }

    if (!modalData.week_days?.some(Boolean)) {
      return "Select at least one day for this standard.";
    }

    if (modalData.type === "staffing") {
      const staffingPeopleCount = parseNullableInteger(
        modalData.staffing_people_count
      );
      if (staffingPeopleCount === null || staffingPeopleCount < 0 ||
          (staffingPeopleCount === 0 && !modalData.is_maximum_staffing)) {
        return modalData.is_maximum_staffing
          ? "Enter a whole people count of zero or greater."
          : "Enter a whole people count greater than zero.";
      }
      if (modalData.is_maximum_staffing && modalData.role_ids?.length) {
        return "Maximum applies to the whole department. Remove role selections before saving.";
      }
      return "";
    }

    const fixedMinutes = parseNullableFloat(modalData.constant_time_minutes);
    if (fixedMinutes !== null && fixedMinutes < 0) {
      return "Fixed time cannot be negative.";
    }

    const variableMinutes = parseNullableFloat(modalData.scalable_time_minutes);
    if (scaleWithDriverChecked) {
      if (!modalData.labor_driver?.uuid) {
        return "Choose a labor driver for variable time.";
      }
      if (variableMinutes === null || variableMinutes <= 0) {
        return "Enter variable time greater than zero.";
      }
    }

    if (
      (fixedMinutes === null || fixedMinutes === 0) &&
      (!scaleWithDriverChecked || variableMinutes === null || variableMinutes === 0)
    ) {
      return "Add fixed time or variable time before saving.";
    }

    return "";
  };

  // labor_standard_uuid,
  //   type,
  //   name,
  //   department_id,
  //   company_time_window_uuid,
  //   company_time_window_name,
  //   dynamic_start_time,
  //   dynamic_end_time,
  //   fixed_start_offset_qhnum,
  //   fixed_end_offset_qhnum,
  //   week_days,
  //   role_ids,
  //   constant_time_minutes,
  //   scalable_time_minutes,
  //   labor_driver_uuid

  function handleSubmit() {
    const validationMessage = validateLaborStandard();
    if (validationMessage) {
      setSubmitError(validationMessage);
      return;
    }

    setSubmitError("");
    const isStaffingStandard = modalData.type === "staffing";
    props.post_labor_standard(
      modalData.uuid,
      modalData.type,
      modalData.name,
      modalData.department?.id,
      modalData.time_window?.uuid,
      modalData.time_window?.name,
      modalData.time_window?.dynamic_start_time,
      modalData.time_window?.dynamic_end_time,
      modalData.time_window?.fixed_start_offset_qhnum,
      modalData.time_window?.fixed_end_offset_qhnum,
      JSON.stringify(modalData.week_days),
      JSON.stringify(modalData.role_ids),
      parseNullableFloat(modalData.constant_time_minutes),
      parseNullableFloat(modalData.scalable_time_minutes),
      modalData.labor_driver?.uuid,
      modalData.labor_program_flag_company?.uuid,
      parseNullableInteger(modalData.staffing_people_count),
      isStaffingStandard ? Boolean(modalData.is_minimum_staffing) : false,
      isStaffingStandard ? Boolean(modalData.is_maximum_staffing) : false
    );
    handleCloseLaborStandardDrawer();
  }

  function DeleteTimeWindow() {
    props.delete_time_window(modalData.time_window?.uuid);
    setModalData((prevData) => ({
      ...prevData,
      time_window: {
        ...createDefaultTimeWindow(),
      },
    }));
    setIsEditingTimeWindow(false);
    setTimeWindowBackup(null);
    setShowCreateTimeWindow(false);
    setSelectedStartTime("");
    setSelectedEndTime("");
  }

  // // console.log("LABOR DRIVERS", props.laborDrivers);

  // // console.log("LABOR STANDARDS", laborStandards);

  const laborStandardsToDisplay =
    environmentType === "sandbox" ? laborStandardsSandbox : laborStandards;
  const laborStandardGroupsToDisplay =
    groupLaborStandardsByProgramFlag(laborStandardsToDisplay, roleNames);
  const fitActionLabel = laborStandardsToDisplay?.length
    ? "Refit this labor model"
    : "Build new labor model";
  const hasSandboxChangedStandards = laborStandardsSandbox?.some(
    (laborStandard) => laborStandard.has_changed
  );
  const hasSandboxNewStandards = laborStandardsSandbox?.some(
    (laborStandard) => laborStandard.is_new
  );
  const hasSandboxChanges =
    hasSandboxChangedStandards || hasSandboxNewStandards;

  const handlePublishedRowClick = (index) => {
    if (publishedRowTooltipTimeoutRef.current) {
      window.clearTimeout(publishedRowTooltipTimeoutRef.current);
    }
    setPublishedRowTooltipIndex(index);
    publishedRowTooltipTimeoutRef.current = window.setTimeout(() => {
      setPublishedRowTooltipIndex(null);
      publishedRowTooltipTimeoutRef.current = null;
    }, 2200);
  };

  const renderLaborCalculation = (laborStandard) => {
    if (laborStandard.type === "staffing") {
      return (
        <div className="grid grid-cols-[72px_minmax(0,1fr)] items-center gap-x-2 gap-y-1.5">
          <span className="inline-flex h-6 items-center justify-center rounded-md bg-emerald-50 px-2 text-xs font-semibold text-emerald-700">
            Coverage
          </span>
          <div className="min-w-0">
            <span className="text-sm font-semibold text-slate-900">
              {laborStandard.staffing_people_count || 0} people
            </span>
            <span className="ml-2 text-xs text-slate-500">
              {laborStandard.is_maximum_staffing
                ? "Maximum cap"
                : laborStandard.is_minimum_staffing
                ? "Minimum floor"
                : "Additive"}
            </span>
          </div>
        </div>
      );
    }

    const constantTimeMinutes = Number(laborStandard.constant_time_minutes);
    const hasFixedTime =
      !Number.isNaN(constantTimeMinutes) && constantTimeMinutes !== 0;
    const hasLaborDriver = Boolean(laborStandard.labor_driver?.name);
    const scalableRate = formatScalableRate(
      laborStandard.scalable_time_minutes,
      laborStandard.labor_driver
    );

    return hasFixedTime || hasLaborDriver ? (
      <div className="grid grid-cols-[72px_minmax(0,1fr)] items-center gap-x-2 gap-y-1.5">
        {hasFixedTime && (
          <>
            <span className="inline-flex h-6 items-center justify-center rounded-md bg-slate-100 px-2 text-xs font-semibold text-slate-600">
              Fixed
            </span>
            <span className="truncate text-sm font-semibold text-slate-900">
              {formatLaborDuration(laborStandard.constant_time_minutes)}
            </span>
          </>
        )}
        {hasLaborDriver && (
          <>
            <span className="inline-flex h-6 items-center justify-center rounded-md bg-violet-100 px-2 text-xs font-semibold text-violet-700">
              Variable
            </span>
            <span className="min-w-0 truncate text-sm text-slate-900">
              <span className="font-semibold">{scalableRate.duration}</span>{" "}
              <span className="text-slate-400">per</span>{" "}
              <span className="font-medium">{scalableRate.basis}</span>
            </span>
          </>
        )}
      </div>
    ) : (
      <span className="text-sm text-slate-400">No labor time</span>
    );
  };

  const modalScalableRate = formatScalableRate(
    modalData.scalable_time_minutes,
    modalData.labor_driver
  );

  return (
    <>
      <div>
        <div className="w-full px-6 mt-6 flex items-start justify-between gap-6">
          {!props.scenarioMode && (
          <div className="w-[240px] rounded-2xl bg-slate-50 p-1">
            <div className="flex w-full">
              {["published", "sandbox"].map((type) => {
                const isActive = environmentType === type;
                return (
                  <button
                    key={type}
                    type="button"
                    className={`w-1/2 py-3 rounded-xl transition ${
                      isActive ? "bg-white shadow" : ""
                    }`}
                    onClick={() => setEnvironmentType(type)}
                  >
                    <p
                      className={`text-sm ${
                        isActive
                          ? "text-violet-700 font-semibold"
                          : "text-slate-500"
                      }`}
                    >
                      {type === "published" ? "Published" : "Sandbox"}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
          )}
          <div className="flex items-start gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative" ref={dropdownLaborStandardRef}>
                {props.readOnly ? null : environmentType === "sandbox" ? (
                  <button
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={isLaborStandardDropdownOpen}
                    className="inline-flex h-10 items-center rounded-lg bg-violet-700 px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-violet-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2"
                    onClick={handleLaborStandardButtonClick}
                  >
                    <AddIcon className="mr-1.5" sx={{ fontSize: 19 }} />
                    Add labor standard
                  </button>
                ) : (
                  <Tooltip
                    title={"Switch to sandbox mode to add labor standards"}
                  >
                    <div className="bg-slate-200 text-slate-400 h-8 w-8 rounded-full flex items-center justify-center">
                      <AddIcon
                        style={{ fontSize: "18px", marginBottom: "1px" }}
                      />
                    </div>
                  </Tooltip>
                )}
                {isLaborStandardDropdownOpen && (
                  <div
                    role="menu"
                    aria-label="Choose labor standard type"
                    className="absolute left-0 top-12 z-50 w-80 max-w-[calc(100vw-3rem)] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl"
                  >
                    <button
                      type="button"
                      role="menuitem"
                      className="flex w-full items-start gap-3 border-b border-slate-200 px-4 py-3.5 text-left transition-colors hover:bg-slate-50 focus:bg-violet-50 focus:outline-none"
                      onClick={() => {
                        setModalData(default_data);
                        setModalEdit(false);
                        handleOpenLaborStandardDrawer("task");
                      }}
                    >
                      <span className="mt-1 h-3 w-3 shrink-0 rounded-full border border-orange-300 bg-orange-200" />
                      <span>
                        <span className="block text-sm font-semibold text-slate-900">
                          Support standard
                        </span>
                        <span className="mt-0.5 block text-xs leading-5 text-slate-500">
                          Use for prep, cleaning, and other non-customer-facing
                          tasks.
                        </span>
                      </span>
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="flex w-full items-start gap-3 border-b border-slate-200 px-4 py-3.5 text-left transition-colors hover:bg-slate-50 focus:bg-violet-50 focus:outline-none"
                      onClick={() => {
                        setModalData(default_data);
                        setModalEdit(false);
                        handleOpenLaborStandardDrawer("routine");
                      }}
                    >
                      <span className="mt-1 h-3 w-3 shrink-0 rounded-full border border-blue-300 bg-blue-200" />
                      <span>
                        <span className="block text-sm font-semibold text-slate-900">
                          Service standard
                        </span>
                        <span className="mt-0.5 block text-xs leading-5 text-slate-500">
                          Use for customer-facing work tied directly to demand.
                        </span>
                      </span>
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="flex w-full items-start gap-3 px-4 py-3.5 text-left transition-colors hover:bg-slate-50 focus:bg-violet-50 focus:outline-none"
                      onClick={() => {
                        setModalData(default_data);
                        setModalEdit(false);
                        handleOpenLaborStandardDrawer("staffing");
                      }}
                    >
                      <span className="mt-1 h-3 w-3 shrink-0 rounded-full border border-emerald-300 bg-emerald-200" />
                      <span>
                        <span className="block text-sm font-semibold text-slate-900">
                          Staffing standard
                        </span>
                        <span className="mt-0.5 block text-xs leading-5 text-slate-500">
                          Use for minimum coverage, additive demand, or a maximum
                          staffing level by time period.
                        </span>
                      </span>
                    </button>
                  </div>
                )}
              </div>
              {!props.readOnly &&
                props.scenarioMode &&
                environmentType === "sandbox" &&
                props.onOpenOptimizationDrawer && (
                  <button
                    type="button"
                    className="inline-flex h-10 items-center rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2"
                    onClick={() => props.onOpenOptimizationDrawer()}
                  >
                    <AutoFixHighOutlinedIcon
                      className="mr-1.5"
                      sx={{ fontSize: 18 }}
                    />
                    {fitActionLabel}
                  </button>
                )}
              {!props.scenarioMode && <Tooltip
                title={`See how the labor model affects different labor metrics`}
              >
                <div
                  className="px-3 h-8 rounded-full flex items-center space-x-0.5 bg-orange-600 hover:bg-orange-700 text-white cursor-pointer"
                  onClick={handleOpenTestModal}
                >
                  <div>
                    <ScienceOutlinedIcon
                      style={{ fontSize: "18px", marginBottom: "2px" }}
                    />
                  </div>
                  <p className="pr-1 text-sm">Evaluate</p>
                </div>
              </Tooltip>}
              {!props.scenarioMode && <Tooltip title={"View labor model history"}>
                <div
                  className="px-3 h-8 rounded-full flex items-center space-x-1 border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 cursor-pointer"
                  onClick={() => props.onOpenPublicationHistory?.()}
                >
                  <HistoryIcon style={{ fontSize: "18px" }} />
                  <p className="pr-1 text-sm">History</p>
                </div>
              </Tooltip>}
              {!props.scenarioMode && environmentType === "sandbox" ? (
                <Tooltip title={`${fitActionLabel} using historical data`}>
                  <div
                    className="px-3 h-8 rounded-full flex items-center space-x-1 bg-emerald-600 hover:bg-emerald-700 text-white cursor-pointer"
                    onClick={() => props.onOpenOptimizationDrawer?.()}
                  >
                    <div>
                      <AutoFixHighOutlinedIcon
                        style={{ fontSize: "18px", marginBottom: "2px" }}
                      />
                    </div>
                    <p className="pr-1 text-sm">{fitActionLabel}</p>
                  </div>
                </Tooltip>
              ) : !props.scenarioMode ? (
                <Tooltip title={"Switch to sandbox mode to refit this labor model"}>
                  <div className="px-3 h-8 rounded-full flex items-center space-x-1 bg-slate-200 text-slate-400">
                    <div>
                      <AutoFixHighOutlinedIcon
                        style={{ fontSize: "18px", marginBottom: "2px" }}
                      />
                    </div>
                    <p className="pr-1 text-sm">{fitActionLabel}</p>
                  </div>
                </Tooltip>
              ) : null}
            </div>
            {!props.scenarioMode && <div className="mx-1 h-8 w-px bg-slate-200"></div>}
            {!props.scenarioMode && <div className="relative w-[132px]">
              {environmentType === "sandbox" ? (
                <>
                  <Tooltip title={"Publish labor model"}>
                    <div
                      className="w-full px-3 h-8 rounded-full flex items-center justify-center space-x-1 bg-violet-700 hover:bg-violet-800 text-white cursor-pointer"
                      onClick={() => props.onOpenPublishWarningModal?.()}
                    >
                      <div>
                        <RocketLaunchOutlinedIcon
                          style={{ fontSize: "18px", marginBottom: "2px" }}
                        />
                      </div>
                      <p className="text-sm">Publish</p>
                    </div>
                  </Tooltip>
                  <Tooltip
                    title={
                      hasSandboxChanges
                        ? "Reset sandbox to the published labor model"
                        : "There are no sandbox changes to reset"
                    }
                  >
                    <div
                      className={`absolute top-10 left-0 w-full px-3 h-8 rounded-full flex items-center justify-center space-x-1 border ${
                        hasSandboxChanges
                          ? "border-slate-200 bg-white text-slate-600 cursor-pointer hover:bg-slate-50"
                          : "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                      }`}
                      onClick={() => {
                        if (!hasSandboxChanges) {
                          return;
                        }
                        props.revert_labor_standard?.();
                      }}
                    >
                      <p className="text-sm">Reset</p>
                    </div>
                  </Tooltip>
                </>
              ) : (
                <Tooltip title={"Switch to sandbox mode to publish labor model"}>
                  <div className="w-full px-3 h-8 rounded-full flex items-center justify-center space-x-1 bg-slate-200 text-slate-400">
                    <div>
                      <RocketLaunchOutlinedIcon
                        style={{ fontSize: "18px", marginBottom: "2px" }}
                      />
                    </div>
                    <p className="text-sm">Publish</p>
                  </div>
                </Tooltip>
              )}
            </div>
            }
          </div>
        </div>
        {!props.embedded && <div className="px-6 mt-10">
          <div className="flex items-center space-x-2">
            <h1 className="text-3xl text-slate-900">
              {props.scenarioMode
                ? props.readOnly ? "Published Labor Standards" : "Labor Standards"
                : environmentType === "published"
                ? "Published"
                : "Sandbox"}
            </h1>
            {environmentType === "published" && (
              <Tooltip title={"Published labor standards are read-only"}>
                <div className="flex items-center text-slate-400">
                  <LockOutlinedIcon style={{ fontSize: "22px" }} />
                </div>
              </Tooltip>
            )}
          </div>
          <p className="mt-2 max-w-4xl text-sm text-slate-500">
            {props.scenarioMode
              ? props.readOnly
                ? "This is the complete read-only set of labor standards captured in this published model."
                : "Create and edit the standards that belong to the selected sandbox scenario. Publishing happens from the controls above and activates the complete model together."
              : environmentType === "published"
              ? 'Published labor standards determine your labor budgets and drive the earned or forecasted labor calculations used across the company. Switch to Sandbox to make changes, then use Evaluate to compare the proposed model before publishing it.'
              : 'Sandbox is where you can create, edit, and test labor standards before they go live. Use Evaluate to compare Sandbox against Published, then Publish when the updated model is ready.'}
          </p>
        </div>}
        <div>
          {environmentType === "sandbox" && hasSandboxChanges && (
            <div className="px-6 mt-5">
              {hasSandboxChangedStandards && (
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-amber-100"></div>
                  <p className="text-sm text-slate-500">
                    Labor standard has been changed from the published model
                  </p>
                </div>
              )}
              {hasSandboxNewStandards && (
                <div
                  className={`flex items-center space-x-2 ${
                    hasSandboxChangedStandards ? "mt-1.5" : ""
                  }`}
                >
                  <div className="w-3 h-3 rounded-full bg-emerald-100"></div>
                  <p className="text-sm text-slate-500">
                    Labor standard is new compared with the published model
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="mt-5 px-6 pb-6 sm:px-4">
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <div className="overflow-x-auto">
                <table className="min-w-[860px] w-full table-fixed">
                  <thead className="border-b border-slate-200 bg-slate-50">
                    <tr>
                      <th
                        scope="col"
                        className="w-[13%] px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500"
                      >
                        Type
                      </th>
                      <th
                        scope="col"
                        className="w-[24%] px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500"
                      >
                        Name
                      </th>
                      <th
                        scope="col"
                        className="w-[36%] px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500"
                      >
                        Labor Calculation
                      </th>
                      {/* <th
                        scope="col"
                        className="flex-1 px-3 py-3.5 text-left text-xs text-slate-500 font-medium"
                      >
                        Driver
                      </th> */}
                      <th
                        scope="col"
                        className="w-[20%] px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500"
                      >
                        When
                      </th>
                      <th scope="col" className="w-[7%] px-4 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {laborStandardGroupsToDisplay.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-6 py-10 text-center text-sm text-slate-500"
                        >
                          No labor standards have been created yet.
                        </td>
                      </tr>
                    ) : (
                      laborStandardGroupsToDisplay.map((group) => (
                        <React.Fragment key={group.key}>
                          <tr className="border-y border-slate-200 bg-slate-50">
                            <td colSpan={5} className="px-5 py-2.5">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                                    {group.label}
                                  </p>
                                </div>
                                <p className="text-xs text-slate-500">
                                  {group.standards.length}{" "}
                                  {group.standards.length === 1
                                    ? "standard"
                                    : "standards"}
                                </p>
                              </div>
                            </td>
                          </tr>
                          {group.standards.map((laborStandard) => {
                            const selectedRoleNames = roleNames.filter(
                              (role) =>
                                laborStandard.role_ids.includes(role.id)
                            );
                            const rowIndex = laborStandard.displayIndex;
                            const selectedDays = getSelectedDays(
                              day_abbrevs,
                              laborStandard.week_days
                            );

                            return (
                              <Tooltip
                                key={laborStandard.uuid || rowIndex}
                                title={
                                  environmentType === "published"
                                    ? "You need to make changes in Sandbox. Published labor standards cannot be edited."
                                    : ""
                                }
                                open={
                                  environmentType === "published" &&
                                  publishedRowTooltipIndex === rowIndex
                                }
                                onClose={() =>
                                  setPublishedRowTooltipIndex(null)
                                }
                                disableHoverListener={
                                  environmentType !== "published"
                                }
                                disableFocusListener={
                                  environmentType !== "published"
                                }
                                disableTouchListener={
                                  environmentType !== "published"
                                }
                              >
                                <tr
                                  className={`group ${
                                    environmentType === "sandbox"
                                      ? laborStandard.is_new
                                        ? "cursor-pointer bg-emerald-50 hover:bg-emerald-100"
                                        : laborStandard.has_changed
                                        ? "cursor-pointer bg-amber-50 hover:bg-amber-100"
                                        : "cursor-pointer hover:bg-violet-50/50"
                                      : "cursor-not-allowed bg-white hover:bg-slate-50"
                                  } transition-colors`}
                                  onClick={() => {
                                    if (environmentType === "sandbox") {
                                      setModalData(laborStandard);
                                      setModalEdit(true);
                                      handleOpenLaborStandardDrawer(
                                        laborStandard.type
                                      );
                                      return;
                                    }
                                    handlePublishedRowClick(rowIndex);
                                  }}
                                >
                                  <td className="whitespace-nowrap px-5 py-4 text-sm">
                                    <div className="w-fit">
                                      <div
                                        className={`inline-flex rounded-md px-2.5 py-1 text-xs font-semibold ${
                                          laborStandard.type === "task"
                                            ? "bg-orange-50 text-orange-700"
                                            : laborStandard.type === "routine"
                                            ? "bg-blue-50 text-blue-700"
                                            : "bg-emerald-50 text-emerald-700"
                                        } items-center justify-center`}
                                      >
                                        <span>
                                          {laborStandard.type === "task"
                                            ? "Support"
                                            : laborStandard.type === "routine"
                                            ? "Service"
                                            : "Staffing"}
                                        </span>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-4 text-sm text-slate-900">
                                    <div>
                                      <p className="truncate text-sm font-semibold text-slate-900" title={laborStandard.name}>
                                        {laborStandard.name}
                                      </p>
                                      {selectedRoleNames?.length > 0 ? (
                                        <div className="mt-0.5">
                                          {selectedRoleNames.map((role) => (
                                            <p
                                              key={role.id}
                                              className="text-slate-500 text-xs"
                                            >
                                              {role.role_name}
                                            </p>
                                          ))}
                                        </div>
                                      ) : (
                                        <p className="text-slate-500 text-xs mt-0.5">
                                          {laborStandard.department?.role_type}
                                        </p>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-4 py-4 text-sm text-slate-900">
                                    {renderLaborCalculation(laborStandard)}
                                  </td>
                                  <td className="px-4 py-4 text-sm text-slate-900">
                                    <div>
                                      <p className="text-slate-900 text-sm">
                                        {laborStandard.time_window?.name}
                                      </p>

                                      {laborStandard.week_days?.includes(
                                        false
                                      ) ? (
                                        <div className="flex space-x-0.5 mt-0.5">
                                          {selectedDays.map((day, index) => (
                                            <p
                                              key={day}
                                              className="text-slate-500 text-xs mt-0.5"
                                            >
                                              {index < selectedDays.length - 1
                                                ? `${day},`
                                                : `${day}`}
                                            </p>
                                          ))}
                                        </div>
                                      ) : (
                                        <p className="text-slate-500 text-xs mt-0.5">
                                          Every day
                                        </p>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-4 py-4 text-right text-sm font-medium">
                                    <div className="flex h-full w-full items-center justify-end text-slate-500">
                                      {environmentType === "sandbox" ? (
                                        <div className="flex h-9 w-9 items-center justify-center rounded-lg group-hover:text-violet-700">
                                          <ChevronRightIcon />
                                        </div>
                                      ) : (
                                        <div className="w-8"></div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              </Tooltip>
                            );
                          })}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </table>
            </div>
          </div>
        </div>
      </div>
      <Drawer
        open={openLaborStandardDrawer}
        anchor="right"
        onClose={handleCloseLaborStandardDrawer}
      >
        <div className="flex h-full w-[560px] max-w-[100vw] flex-col overflow-hidden">
          <div className="min-h-0 flex-1 overflow-y-auto">
            <DrawerHeader
              title={
                drawerType === "task"
                  ? modalEdit
                    ? "Edit support standard"
                    : "Add support standard"
                  : drawerType === "routine"
                  ? modalEdit
                  ? "Edit service standard"
                  : "Add service standard"
                  : modalEdit
                  ? "Edit staffing standard"
                  : "Add staffing standard"
              }
              onClose={handleCloseLaborStandardDrawer}
            />
            <div className="w-full px-6">
              <div className="pb-6 border-b border-slate-200">
                <div className="text-slate-500">
                  {drawerType === "task" ? (
                    <p>This standard will be applied once per day</p>
                  ) : drawerType === "routine" ? (
                    <p>This will be applied continuously throughout the day</p>
                  ) : (
                    <p>This sets a staffing floor, additive demand, or a department cap across the selected time window</p>
                  )}
                </div>
                <div className="mt-5">
                  <p className="text-sm text-slate-500">Name</p>
                  <div className="mt-1">
                    <TextField
                      placeholder={
                        drawerType === "task"
                          ? "Morning routine"
                          : drawerType === "routine"
                          ? "Serving a customer"
                          : "Frontline minimum"
                      }
                      fullWidth
                      variant="outlined"
                      size="small"
                      name="name"
                      value={modalData.name}
                      onChange={handleChangeLayer1}
                      inputProps={{ maxLength: 255 }}
                      InputProps={{
                        sx: {
                          backgroundColor: "white",
                          borderRadius: "8px",
                          padding: "5px",
                          height: "45px",
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#6d28d9",
                          },
                        },
                      }}
                    />
                  </div>
                </div>
                <div className="mt-5">
                  <p className="text-sm text-slate-500">Assign to department</p>
                  <div className="mt-1">
                    {(() => {
                      const departmentOptions = props.roleTypes || [];
                      const selectedDepartmentValue = modalData.department?.role_type;
                      const departmentValue = departmentOptions.some(
                        (department) => department.role_type === selectedDepartmentValue
                      )
                        ? selectedDepartmentValue
                        : "";

                      return (
                    <Select
                      fullWidth
                      value={departmentValue}
                      name="role_type"
                      onChange={handleDepartmentChange}
                      size="small"
                      sx={{
                        borderRadius: "8px",
                        paddingY: "1px",
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6d28d9",
                        },
                        height: "45px",
                        backgroundColor: "white",
                      }}
                    >
                      {departmentOptions.map((department, index) => (
                        <MenuItem key={index} value={department.role_type}>
                          {department.role_type}
                        </MenuItem>
                      ))}
                    </Select>
                      );
                    })()}
                  </div>
                </div>
                {modalData.department?.role_type && (
                  <div className="mt-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-900">Role selection</p>
                        <p className="text-xs text-slate-500 mt-1">{`Is this ${getStandardTypeLabel(drawerType)} standard assigned to specific roles?`}</p>
                      </div>
                      <div>
                        <PurpleSwitch
                          checked={roleSelectionChecked}
                          onChange={handleRoleSelectionChecked}
                        />
                      </div>
                    </div>
                    {roleSelectionChecked && (
                      <div className="mt-4">
                        <p className="text-sm text-slate-500">
                          Assign to roles
                        </p>
                        <div className="mt-1">
                          <Select
                            fullWidth
                            multiple
                            value={modalData.role_ids}
                            name="role_names"
                            onChange={handleRoleSelectionChange}
                            size="small"
                            sx={{
                              borderRadius: "8px",
                              paddingY: "1px",
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "#6d28d9",
                                },
                              height: "45px",
                              backgroundColor: "white",
                            }}
                            renderValue={(selected) => {
                              const selectedRoleNames = roleNames
                                .filter((role) => selected.includes(role.id))
                                .map((role) => role.role_name);

                              if (selectedRoleNames.length <= 4) {
                                return selectedRoleNames.join(", ");
                              } else {
                                return `${selectedRoleNames[0]}, ${selectedRoleNames[1]}, ${selectedRoleNames[2]}, ${selectedRoleNames[3]} ...`;
                              }
                            }}
                          >
                            {filteredRoleNames.map((role, index) => (
                              <MenuItem key={index} value={role.id}>
                                <Checkbox
                                  checked={
                                    modalData.role_ids?.indexOf(role.id) > -1
                                  }
                                  sx={{
                                    color: "#6d28d9",
                                    "&.Mui-checked": {
                                      color: "#6d28d9",
                                    },
                                  }}
                                />
                                <ListItemText primary={role.role_name} />
                              </MenuItem>
                            ))}
                          </Select>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="mt-6 pb-6 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-900">Applies to</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Company-wide by default. Turn on to apply this standard only
                      to stores in a labor model group.
                    </p>
                  </div>
                  <div>
                    <PurpleSwitch
                      checked={locationSelectionChecked}
                      onChange={handleLocationSelectionChecked}
                    />
                  </div>
                </div>
                {locationSelectionChecked && (
                  <div className="mt-4">
                    <p className="text-xs text-slate-500">Store group</p>
                    <div className="mt-1">
                      <Select
                        fullWidth
                        value={modalData.labor_program_flag_company?.name}
                        name="program_flag"
                        onChange={handleFlagChange}
                        size="small"
                        sx={{
                          borderRadius: "8px",
                          paddingY: "1px",
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#6d28d9",
                          },
                          height: "45px",
                          backgroundColor: "white",
                        }}
                      >
                        {props.laborProgramFlags?.map((flag, index) => (
                          <MenuItem key={index} value={flag.name}>
                            {flag.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </div>
                  </div>
                )}
              </div>
              {drawerType === "staffing" ? (
                <div className="mt-6 pb-6 border-b border-slate-200">
                  <div className="flex justify-between">
                    <div>
                      <p className="text-slate-900">People count</p>
                      <p className="text-slate-500 text-xs mt-1">
                        Number of team members required in this window
                      </p>
                    </div>
                    <div className="w-32">
                      <TextField
                        placeholder="2"
                        fullWidth
                        variant="outlined"
                        size="small"
                        name="staffing_people_count"
                        value={modalData.staffing_people_count}
                        onChange={handleChangeLayer1}
                        InputProps={{
                          sx: {
                            backgroundColor: "white",
                            borderRadius: "8px",
                            paddingY: "5px",
                            height: "45px",
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#6d28d9",
                            },
                          },
                        }}
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="w-full bg-slate-50 rounded-xl p-4">
                      <div className="flex justify-between gap-4 sm:flex-col">
                        <div>
                          <label id="staffing-mode-label" htmlFor="staffing-mode" className="text-slate-900 text-sm">
                            Staffing behavior
                          </label>
                          <p className="text-slate-500 text-xs mt-1">
                            Maximum caps modeled requirements for the whole department after demand and minimums.
                            It does not cap scheduled headcount. Conflicting minimums must be resolved.
                          </p>
                        </div>
                        <div className="w-36 shrink-0 sm:w-full">
                          <RightworkSelect
                            id="staffing-mode"
                            value={modalData.is_maximum_staffing ? "maximum" : modalData.is_minimum_staffing ? "minimum" : "additive"}
                            onChange={handleStaffingModeChange}
                            options={[
                              { value: "minimum", label: "Minimum" },
                              { value: "additive", label: "Additive" },
                              { value: "maximum", label: "Maximum" },
                            ]}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-6 pb-6 border-b border-slate-200">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      How this standard earns labor
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      Combine a fixed amount of work with optional time that
                      scales as the selected driver increases.
                    </p>
                  </div>

                  <div className="mt-4 grid grid-cols-[minmax(0,1fr)_180px] items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-1">
                    <div>
                      <label
                        htmlFor="labor-standard-fixed-minutes"
                        className="text-sm font-semibold text-slate-900"
                      >
                        Fixed time
                      </label>
                      <p className="mt-1 text-xs leading-5 text-slate-500">
                        Minutes earned each time this standard applies.
                      </p>
                    </div>
                    <div>
                      <TextField
                        id="labor-standard-fixed-minutes"
                        placeholder="60"
                        fullWidth
                        variant="outlined"
                        size="small"
                        name="constant_time_minutes"
                        value={modalData.constant_time_minutes}
                        onChange={handleChangeLayer1}
                        inputProps={{
                          "aria-label": "Fixed time in minutes",
                          inputMode: "decimal",
                        }}
                        InputProps={{
                          sx: {
                            backgroundColor: "white",
                            borderRadius: "8px",
                            height: "44px",
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#6d28d9",
                            },
                          },
                          endAdornment: (
                            <InputAdornment position="end">min</InputAdornment>
                          ),
                        }}
                      />
                    </div>
                  </div>

                  <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">
                            Variable time
                          </p>
                          <p className="mt-1 text-xs leading-5 text-slate-500">
                            Add labor as a sales or operating driver increases.
                          </p>
                        </div>
                        <div>
                          <PurpleSwitch
                            checked={scaleWithDriverChecked}
                            onChange={handleScaleWithDriverChecked}
                          />
                        </div>
                    </div>
                    {scaleWithDriverChecked && (
                      <div className="mt-4 border-t border-slate-200 pt-4">
                          <div className="grid grid-cols-[minmax(0,1fr)_210px] gap-3 sm:grid-cols-1">
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Driver
                                </label>
                                <div className="mt-1.5">
                                  <Select
                                    fullWidth
                                    value={modalData.labor_driver?.name}
                                    name="name"
                                    onChange={handleChangeLaborDriver}
                                    size="small"
                                    sx={{
                                      borderRadius: "8px",
                                      paddingY: "1px",
                                      "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                        {
                                          borderColor: "#6d28d9",
                                        },
                                      height: "44px",
                                      backgroundColor: "white",
                                    }}
                                    inputProps={{
                                      "aria-label": "Variable time labor driver",
                                    }}
                                  >
                                    {props.laborDrivers?.map(
                                      (laborDriver, index) => (
                                        <MenuItem
                                          key={index}
                                          value={laborDriver.name}
                                        >
                                          {laborDriver.name}
                                        </MenuItem>
                                      )
                                    )}
                                  </Select>
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  {isRevenueLaborDriver(modalData.labor_driver)
                                    ? "Minutes per $100"
                                    : "Minutes per unit"}
                                </label>
                                <div className="mt-1.5">
                                  <TextField
                                    placeholder={
                                      isRevenueLaborDriver(
                                        modalData.labor_driver
                                      )
                                        ? "0.3"
                                        : "1"
                                    }
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    name="scalable_time_minutes"
                                    value={scalableTimeInputValue}
                                    onChange={handleScalableTimeDisplayChange}
                                    onFocus={() => {
                                      scalableTimeInputFocusedRef.current = true;
                                    }}
                                    onBlur={handleScalableTimeDisplayBlur}
                                    inputProps={{
                                      "aria-label": isRevenueLaborDriver(
                                        modalData.labor_driver
                                      )
                                        ? "Variable minutes per one hundred dollars"
                                        : "Variable minutes per driver unit",
                                      inputMode: "decimal",
                                    }}
                                    InputProps={{
                                      sx: {
                                        backgroundColor: "white",
                                        borderRadius: "8px",
                                        height: "44px",
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                          {
                                            borderColor: "#6d28d9",
                                          },
                                      },
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          {getScalableInputAdornment(
                                            modalData.labor_driver
                                          )}
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                </div>
                            </div>
                          </div>
                          {modalData.labor_driver?.name && (
                            <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5">
                                <p className="text-sm text-emerald-800">
                                  Each{" "}
                                  <span className="font-bold">
                                    {modalScalableRate.basis}
                                  </span>{" "}
                                  adds{" "}
                                  <span className="font-bold">
                                    {scalableTimeInputValue
                                      ? modalScalableRate.duration
                                      : "x min"}
                                  </span>{" "}
                                </p>
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="mt-6">
                <div>
                  <p className="text-slate-900 text-sm">
                    When the standard is eligible to be done
                  </p>
                  <p className="text-slate-500 text-xs mt-1">
                    Select a time window or{" "}
                    <span
                      className="border-b border-violet-700 hover:border-violet-800 text-violet-700 hover:text-violet-800 cursor-pointer"
                      onClick={handleOpenCreateTimeWindow}
                    >
                      create one
                    </span>
                  </p>
                </div>
                {!showCreateTimeWindow ? (
                  <div>
                    <div className="mt-3">
                      <p className="text-xs text-slate-500">Time window</p>
                      <div className="mt-1">
                        <Select
                          fullWidth
                          value={modalData.time_window?.name}
                          name="name"
                          onChange={handleTimeWindowChange}
                          size="small"
                          sx={{
                            borderRadius: "8px",
                            paddingY: "1px",
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#6d28d9",
                            },
                            height: "38px",
                            backgroundColor: "white",
                          }}
                        >
                          {props.companyTimeWindows?.map((item, index) => (
                            <MenuItem key={index} value={item.name}>
                              {item.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </div>
                    </div>
                    {modalData.time_window?.name && (
                      <div className="mt-3">
                        <div className="w-full bg-slate-50 rounded-xl p-4">
                          <div className="flex justify-between">
                            <p className="text-slate-900 text-sm font-semibold">
                              Selected time window
                            </p>
                            <div className="flex items-center space-x-3">
                              <div
                                className="text-slate-500 hover:text-violet-700 cursor-pointer"
                                onClick={handleEditExistingTimeWindow}
                              >
                                <Tooltip
                                  title={`Edit "${modalData.time_window?.name}" time window option.`}
                                >
                                  <EditOutlinedIcon style={{ fontSize: "20px" }} />
                                </Tooltip>
                              </div>
                              <div
                                className="text-slate-500 hover:text-rose-800 cursor-pointer"
                                onClick={() => DeleteTimeWindow()}
                              >
                                <Tooltip
                                  title={`Delete "${modalData.time_window?.name}" time window option. Can only delete if not in use.`}
                                >
                                  <DeleteOutlineIcon
                                    style={{ fontSize: "20px" }}
                                  />
                                </Tooltip>
                              </div>
                            </div>
                          </div>
                          <div className="mt-0.5">
                            <p className="text-slate-500 text-xs">Name</p>
                            <p className="text-slate-900 mt-0.5 text-sm">
                              {modalData.time_window?.name}
                            </p>
                          </div>
                          {modalData.time_window?.dynamic_start_time ===
                          "not_dynamic" ? (
                            <div className="w-full flex mt-2">
                              <div className="w-1/2">
                                <p className="text-slate-500 text-xs">
                                  Start time
                                </p>
                                <p className="text-slate-900 mt-0.5 text-sm">
                                  {get_t_str(
                                    modalData.time_window
                                      ?.fixed_start_offset_qhnum
                                  )}
                                </p>
                              </div>
                            </div>
                          ) : (
                            <div className="w-full flex mt-2">
                              <div className="w-1/2">
                                <p className="text-slate-500 text-xs">
                                  Start time
                                </p>
                                <p className="text-slate-900 mt-0.5 text-sm">
                                  {modalData.time_window?.dynamic_start_time ===
                                  "start"
                                    ? "Open hours"
                                    : modalData.time_window
                                        ?.dynamic_start_time === "close"
                                    ? "Close hours"
                                    : ""}
                                </p>
                              </div>
                              <div className="w-1/2">
                                <p className="text-slate-500 text-xs">
                                  Start time buffer
                                </p>
                                <p className="text-slate-900 mt-0.5 text-sm">
                                  {
                                    modalData.time_window
                                      ?.fixed_start_offset_qhnum
                                  }
                                </p>
                              </div>
                            </div>
                          )}
                          {modalData.time_window?.dynamic_end_time ===
                          "not_dynamic" ? (
                            <div className="w-full flex mt-2">
                              <div className="w-1/2">
                                <p className="text-slate-500 text-xs">
                                  End time
                                </p>
                                <p className="text-slate-900 mt-0.5 text-sm">
                                  {get_t_str(
                                    modalData.time_window
                                      ?.fixed_end_offset_qhnum
                                  )}
                                </p>
                              </div>
                            </div>
                          ) : (
                            <div className="w-full flex mt-2">
                              <div className="w-1/2">
                                <p className="text-slate-500 text-xs">
                                  End time
                                </p>
                                <p className="text-slate-900 mt-0.5 text-sm">
                                  {modalData.time_window?.dynamic_end_time ===
                                  "start"
                                    ? "Open hours"
                                    : modalData.time_window
                                        ?.dynamic_end_time === "close"
                                    ? "Close hours"
                                    : ""}
                                </p>
                              </div>
                              <div className="w-1/2">
                                <p className="text-slate-500 text-xs">
                                  End time buffer
                                </p>
                                <p className="text-slate-900 mt-0.5 text-sm">
                                  {
                                    modalData.time_window
                                      ?.fixed_end_offset_qhnum
                                  }
                                </p>
                              </div>
                            </div>
                          )}
                          <div className="mt-2.5">
                            <div className="w-full rounded-lg border border-emerald-200 text-emerald-800 bg-emerald-50 text-xs py-1.5 px-3">
                              <p>
                                Starts at{" "}
                                {modalData.time_window?.dynamic_start_time ===
                                "not_dynamic" ? (
                                  <span className="font-bold">
                                    {get_t_str(
                                      modalData.time_window
                                        ?.fixed_start_offset_qhnum
                                    )}
                                  </span>
                                ) : (
                                  <span className="font-bold">
                                    {modalData.time_window
                                      ?.fixed_start_offset_qhnum !== 0
                                      ? `${Math.abs(
                                          modalData.time_window
                                            ?.fixed_start_offset_qhnum
                                        )} hours ${
                                          modalData.time_window
                                            ?.fixed_start_offset_qhnum > 0
                                            ? "after"
                                            : "before"
                                        }`
                                      : ""}{" "}
                                    {modalData.time_window
                                      ?.dynamic_start_time === "start"
                                      ? "Open hours"
                                      : "Close hours"}
                                  </span>
                                )}{" "}
                                and finishes at{" "}
                                {modalData.time_window?.dynamic_end_time ===
                                "not_dynamic" ? (
                                  <span className="font-bold">
                                    {get_t_str(
                                      modalData.time_window
                                        ?.fixed_end_offset_qhnum
                                    )}
                                  </span>
                                ) : (
                                  <span className="font-bold">
                                    {modalData.time_window
                                      ?.fixed_end_offset_qhnum !== 0
                                      ? `${Math.abs(
                                          modalData.time_window
                                            ?.fixed_end_offset_qhnum
                                        )} hours ${
                                          modalData.time_window
                                            ?.fixed_end_offset_qhnum > 0
                                            ? "after"
                                            : "before"
                                        }`
                                      : ""}{" "}
                                    {modalData.time_window?.dynamic_end_time ===
                                    "start"
                                      ? "Open hours"
                                      : "Close hours"}
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mt-4">
                    <div className="w-full bg-slate-50 rounded-xl p-4">
                      <div className="flex justify-between">
                        <div>
                          <p className="text-slate-900 text-sm font-semibold">
                            {isEditingTimeWindow
                              ? "Edit time window"
                              : "Create new time window"}
                          </p>
                          <p className="text-slate-500 text-xs mt-1">
                            {isEditingTimeWindow
                              ? "Update the name and timing for this time window."
                              : "You can use your standard open and close hours or specifiy a custom time"}
                          </p>
                        </div>
                        <div
                          className="text-slate-500 hover:text-rose-800 cursor-pointer ml-4 mt-4"
                          onClick={handleCloseCreateTimeWindow}
                        >
                          <Tooltip
                            title={
                              isEditingTimeWindow
                                ? "Cancel editing this time window"
                                : "Cancel create new time window"
                            }
                          >
                            <DeleteOutlineIcon />
                          </Tooltip>
                        </div>
                      </div>
                      <div className="mt-4 pb-4 border-b border-slate-200">
                        <p className="text-xs text-slate-500">Name</p>
                        <div className="mt-1">
                          <TextField
                            placeholder="Time window name"
                            fullWidth
                            variant="outlined"
                            size="small"
                            name="name"
                            value={modalData.time_window?.name}
                            onChange={handleCreateTimeWindow}
                            InputProps={{
                              sx: {
                                backgroundColor: "white",
                                borderRadius: "8px",
                                padding: "5px",
                                height: "38px",
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                  {
                                    borderColor: "#6d28d9",
                                  },
                              },
                            }}
                          />
                        </div>
                      </div>
                      <div className="mt-3 pb-4 border-b border-slate-200">
                        <p className="text-xs text-slate-500">Start time</p>
                        <div className="bg-white border border-[#bdbdbd] rounded-xl overflow-hidden mt-1">
                          <div
                            className="border-b border-[#bdbdbd] hover:bg-violet-50 px-4 h-10 flex flex-row items-center justify-between cursor-pointer"
                            onClick={() =>
                              setSelectedStartTimeAndUpdateDynamicTime("start")
                            }
                          >
                            <p
                              className={`${
                                selectedStartTime === "start"
                                  ? "text-violet-700"
                                  : "text-slate-900"
                              } text-sm`}
                            >
                              Open hours
                            </p>
                            {selectedStartTime === "start" && (
                              <div className="text-violet-700">
                                <CheckIcon color="#6d28d9" />
                              </div>
                            )}
                          </div>
                          <div
                            className="border-b border-[#bdbdbd] hover:bg-violet-50 px-4 h-10 flex flex-row items-center justify-between cursor-pointer"
                            onClick={() =>
                              setSelectedStartTimeAndUpdateDynamicTime("close")
                            }
                          >
                            <p
                              className={`${
                                selectedStartTime === "close"
                                  ? "text-violet-700"
                                  : "text-slate-900"
                              } text-sm`}
                            >
                              Close hours
                            </p>
                            {selectedStartTime === "close" && (
                              <div className="text-violet-700">
                                <CheckIcon color="#6d28d9" />
                              </div>
                            )}
                          </div>
                          <div
                            className="px-4 h-10 hover:bg-violet-50 flex flex-row items-center justify-between cursor-pointer"
                            onClick={() =>
                              setSelectedStartTimeAndUpdateDynamicTime(
                                "not_dynamic"
                              )
                            }
                          >
                            <p
                              className={`${
                                selectedStartTime === "not_dynamic"
                                  ? "text-violet-700"
                                  : "text-slate-900"
                              } text-sm`}
                            >
                              Custom
                            </p>
                            {selectedStartTime === "not_dynamic" && (
                              <div className="text-violet-700">
                                <CheckIcon color="#6d28d9" />
                              </div>
                            )}
                          </div>
                        </div>
                        {selectedStartTime === "start" ||
                        selectedStartTime === "close" ? (
                          <div className="mt-4">
                            <p className="text-xs text-slate-500">Buffer</p>
                            <div className="mt-1">
                              <Select
                                fullWidth
                                value={
                                  modalData.time_window.fixed_start_offset_qhnum
                                }
                                name="fixed_start_offset_qhnum"
                                onChange={handleTimeWindowStartStopChange}
                                size="small"
                                sx={{
                                  borderRadius: "8px",
                                  paddingY: "1px",
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                      borderColor: "#6d28d9",
                                    },
                                  height: "38px",
                                  backgroundColor: "white",
                                }}
                              >
                                {generateBufferOptionsDF().map((v) => (
                                  <MenuItem key={v.value} value={v.value}>
                                    {v.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </div>
                          </div>
                        ) : selectedStartTime === "not_dynamic" ? (
                          <div className="mt-4">
                            <p className="text-xs text-slate-500">Time</p>
                            <div className="mt-1">
                              <Select
                                fullWidth
                                value={
                                  modalData.time_window.fixed_start_offset_qhnum
                                }
                                name="fixed_start_offset_qhnum"
                                onChange={handleTimeWindowStartStopChange}
                                size="small"
                                sx={{
                                  borderRadius: "8px",
                                  paddingY: "1px",
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                      borderColor: "#6d28d9",
                                    },
                                  height: "38px",
                                  backgroundColor: "white",
                                }}
                              >
                                {generateTimeOptionsDF(0).map((v) => (
                                  <MenuItem key={v.value} value={v.value}>
                                    {v.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </div>
                          </div>
                        ) : (
                          <div></div>
                        )}
                      </div>
                      <div className="mt-4">
                        <p className="text-xs text-slate-500">End time</p>
                        <div className="bg-white border border-[#bdbdbd] rounded-xl overflow-hidden mt-1">
                          <div
                            className="border-b border-[#bdbdbd] hover:bg-violet-50 px-4 h-10 flex flex-row items-center justify-between cursor-pointer"
                            onClick={() =>
                              setSelectedEndTimeAndUpdateDynamicTime("start")
                            }
                          >
                            <p
                              className={`${
                                selectedEndTime === "start"
                                  ? "text-violet-700"
                                  : "text-slate-900"
                              } text-sm`}
                            >
                              Open hours
                            </p>
                            {selectedEndTime === "start" && (
                              <div className="text-violet-700">
                                <CheckIcon color="#6d28d9" />
                              </div>
                            )}
                          </div>
                          <div
                            className="border-b border-[#bdbdbd] hover:bg-violet-50 px-4 h-10 flex flex-row items-center justify-between cursor-pointer"
                            onClick={() =>
                              setSelectedEndTimeAndUpdateDynamicTime("close")
                            }
                          >
                            <p
                              className={`${
                                selectedEndTime === "close"
                                  ? "text-violet-700"
                                  : "text-slate-900"
                              } text-sm`}
                            >
                              Close hours
                            </p>
                            {selectedEndTime === "close" && (
                              <div className="text-violet-700">
                                <CheckIcon color="#6d28d9" />
                              </div>
                            )}
                          </div>
                          <div
                            className="px-4 h-10 hover:bg-violet-50 flex flex-row items-center justify-between cursor-pointer"
                            onClick={() =>
                              setSelectedEndTimeAndUpdateDynamicTime(
                                "not_dynamic"
                              )
                            }
                          >
                            <p
                              className={`${
                                selectedEndTime === "not_dynamic"
                                  ? "text-violet-700"
                                  : "text-slate-900"
                              } text-sm`}
                            >
                              Custom
                            </p>
                            {selectedEndTime === "not_dynamic" && (
                              <div className="text-violet-700">
                                <CheckIcon color="#6d28d9" />
                              </div>
                            )}
                          </div>
                        </div>
                        {selectedEndTime === "start" ||
                        selectedEndTime === "close" ? (
                          <div className="mt-4">
                            <p className="text-xs text-slate-500">Buffer</p>
                            <div className="mt-1">
                              <Select
                                fullWidth
                                value={
                                  modalData.time_window.fixed_end_offset_qhnum
                                }
                                name="fixed_end_offset_qhnum"
                                onChange={handleTimeWindowStartStopChange}
                                size="small"
                                sx={{
                                  borderRadius: "8px",
                                  paddingY: "1px",
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                      borderColor: "#6d28d9",
                                    },
                                  height: "38px",
                                  backgroundColor: "white",
                                }}
                              >
                                {generateBufferOptionsDF().map((v) => (
                                  <MenuItem key={v.value} value={v.value}>
                                    {v.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </div>
                          </div>
                        ) : selectedEndTime === "not_dynamic" ? (
                          <div className="mt-4">
                            <p className="text-xs text-slate-500">Time</p>
                            <div className="mt-1">
                              <Select
                                fullWidth
                                value={
                                  modalData.time_window.fixed_end_offset_qhnum
                                }
                                name="fixed_end_offset_qhnum"
                                onChange={handleTimeWindowStartStopChange}
                                size="small"
                                sx={{
                                  borderRadius: "8px",
                                  paddingY: "1px",
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                      borderColor: "#6d28d9",
                                    },
                                  height: "38px",
                                  backgroundColor: "white",
                                }}
                              >
                                {generateTimeOptionsDF(0).map((v) => (
                                  <MenuItem key={v.value} value={v.value}>
                                    {v.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </div>
                          </div>
                        ) : (
                          <div></div>
                        )}
                      </div>
                      {modalData.time_window?.dynamic_start_time &&
                        modalData.time_window?.dynamic_end_time && (
                          <div className="mt-3">
                            <div className="w-full rounded-lg border border-emerald-200 text-emerald-800 bg-emerald-50 text-xs py-1.5 px-3">
                              <p>
                                Starts at{" "}
                                {modalData.time_window?.dynamic_start_time ===
                                "not_dynamic" ? (
                                  <span className="font-bold">
                                    {get_t_str(
                                      modalData.time_window
                                        ?.fixed_start_offset_qhnum
                                    )}
                                  </span>
                                ) : (
                                  <span className="font-bold">
                                    {modalData.time_window
                                      ?.fixed_start_offset_qhnum !== 0
                                      ? `${Math.abs(
                                          modalData.time_window
                                            ?.fixed_start_offset_qhnum
                                        )} hours ${
                                          modalData.time_window
                                            ?.fixed_start_offset_qhnum > 0
                                            ? "after"
                                            : "before"
                                        }`
                                      : ""}{" "}
                                    {modalData.time_window
                                      ?.dynamic_start_time === "start"
                                      ? "Open hours"
                                      : "Close hours"}
                                  </span>
                                )}{" "}
                                and finishes at{" "}
                                {modalData.time_window?.dynamic_end_time ===
                                "not_dynamic" ? (
                                  <span className="font-bold">
                                    {get_t_str(
                                      modalData.time_window
                                        ?.fixed_end_offset_qhnum
                                    )}
                                  </span>
                                ) : (
                                  <span className="font-bold">
                                    {modalData.time_window
                                      ?.fixed_end_offset_qhnum !== 0
                                      ? `${Math.abs(
                                          modalData.time_window
                                            ?.fixed_end_offset_qhnum
                                        )} hours ${
                                          modalData.time_window
                                            ?.fixed_end_offset_qhnum > 0
                                            ? "after"
                                            : "before"
                                        }`
                                      : ""}{" "}
                                    {modalData.time_window?.dynamic_end_time ===
                                    "start"
                                      ? "Open hours"
                                      : "Close hours"}
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                        )}
                      {isEditingTimeWindow && (
                        <div className="mt-6 flex justify-end">
                          <LoadingButton
                            variant="contained"
                            loading={timeWindowSaving}
                            loadingPosition="end"
                            endIcon={<SaveIcon />}
                            onClick={handleSaveEditedTimeWindow}
                            sx={{
                              textTransform: "none",
                              borderRadius: "12px",
                              paddingY: "10px",
                              backgroundColor: "#6d28d9",
                              "&:hover": {
                                backgroundColor: "#5b21b6",
                              },
                            }}
                          >
                            Save time window
                          </LoadingButton>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-6 border-t border-slate-200">
                <div className="mt-6 pb-10">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-900">Day selection</p>
                      <p className="text-xs text-slate-500 mt-1">
                        Is this {getStandardTypeLabel(drawerType)} standard only performed on certain days of
                        the week?
                      </p>
                    </div>
                    <div>
                      <PurpleSwitch
                        checked={daySelectionChecked}
                        onChange={handleDaySelectionChecked}
                      />
                    </div>
                  </div>
                  {daySelectionChecked && (
                    <div className="mt-4">
                      <div>{appliedToButtonGroup()}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="shrink-0 border-t border-slate-200 bg-white px-6 py-4 shadow-[0_-8px_24px_rgba(15,23,42,0.06)]">
            {submitError && (
              <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2">
                <p className="text-sm text-rose-700">{submitError}</p>
              </div>
            )}
            <div className="flex items-center justify-between gap-3">
              {modalEdit ? (
                <button
                  type="button"
                  className="h-11 rounded-lg px-4 text-sm font-semibold text-rose-700 transition-colors hover:bg-rose-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-600"
                  onClick={() => {
                    props.delete_labor_standard(modalData.uuid);
                    handleCloseLaborStandardDrawer();
                  }}
                >
                  Delete standard
                </button>
              ) : (
                <button
                  type="button"
                  className="h-11 rounded-lg border border-slate-300 bg-white px-5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2"
                  onClick={handleCloseLaborStandardDrawer}
                >
                  Cancel
                </button>
              )}
              <div className="min-w-[210px] sm:min-w-0 sm:flex-1">
                <LoadingButton
                  variant="contained"
                  // loading={savingLoading}
                  loadingPosition="end"
                  endIcon={<SaveIcon />}
                  // disabled={!checkIfValid()}
                  onClick={() => handleSubmit()}
                  sx={{
                    width: "100%",
                    textTransform: "none",
                    borderRadius: "8px",
                    height: "44px",
                    backgroundColor: "#6d28d9",
                    "&:hover": {
                      backgroundColor: "#5b21b6",
                    },
                    fontSize: "14px",
                    fontWeight: 600,
                  }}
                >
                  {modalEdit ? "Save changes" : "Save labor standard"}
                </LoadingButton>
              </div>
            </div>
          </div>
        </div>
      </Drawer>
      <LaborModelTestModal
        open={openTestModal}
        handleClose={handleCloseTestModal}
        company_id={props.company_id}
        storeNames={props.storeNames}
        homeStore={props.homeStore}
        roleTypes={props.roleTypes}
      />
    </>
  );
}

export default LaborModelInnerDisplay;
